import React, { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { C } from "../constants/design";
import { FLAG, DEF_RES, LIVE_MS } from "../constants/matches";
import { LiveBadge } from "./ui/atoms";
import { getBetPoints } from "../engine/playerEngine";

const koTime = (m: { koUTC: string }) => new Date(m.koUTC).getTime();

const isLocked = (
  m: { id: string; koUTC: string; status?: string },
  res: Record<string, unknown>
) =>
  !!res[m.id] ||
  (m.status ? m.status !== "scheduled" : Date.now() >= koTime(m));

const isLive = (
  m: { id: string; koUTC: string; status?: string },
  res: Record<string, unknown>
) =>
  !res[m.id] &&
  Date.now() >= koTime(m) &&
  Date.now() < koTime(m) + LIVE_MS;

const kstTime = (m: { koUTC: string }) =>
  new Date(m.koUTC).toLocaleTimeString("en-US", {
    timeZone: "Asia/Seoul",
    hour: "2-digit",
    minute: "2-digit",
  });

const outFromScore = (a: number, b: number) =>
  a > b ? "team1" : a < b ? "team2" : "draw";

const pickPts = getBetPoints;

const parseResScore = (s?: string): [string, string] | null => {
  if (!s) return null;
  const parts = s.split(/[–—\-]/);
  if (parts.length >= 2) return [parts[0].trim(), parts[1].trim()];
  return null;
};

const Fl = ({ team, size = 28 }: { team: string; size?: number }) => (
  <span style={{ fontSize: size, lineHeight: 1, userSelect: "none" }}>
    {FLAG[team] || "🏳️"}
  </span>
);

interface Match {
  id: string;
  grp: string;
  date: string;
  koUTC: string;
  t1: string;
  t2: string;
  p1: number;
  pX: number;
  p2: number;
  status?: string;
}

interface MatchResult {
  o: string;
  s?: string;
}

export function MatchCard({
  m,
  pick,
  results,
  onBet,
  referee,
  onSetResult,
  refVals,
  onRefScore,
  predVals,
  onPredScore,
}: {
  m: Match;
  pick?: string;
  results: Record<string, MatchResult>;
  onBet: (id: string, outcome: string, a: number, b: number) => void;
  referee?: boolean;
  onSetResult?: (id: string, outcome: string, score: string) => void;
  refVals?: { a: string; b: string };
  onRefScore?: (field: string, value: string) => void;
  predVals?: { a: string; b: string };
  onPredScore?: (field: string, value: string) => void;
}) {
  const { t } = useI18n();
  const sA = predVals?.a ?? "";
  const sB = predVals?.b ?? "";
  const setSA = (v: string) => onPredScore?.("a", v);
  const setSB = (v: string) => onPredScore?.("b", v);

  const res = results[m.id];
  const live = isLive(m, results);
  const locked = isLocked(m, results);
  const won = !!(res && pick && res.o === pick);
  const lost = !!(res && pick && res.o !== pick);
  const noBet = !!(res && !pick);
  const canRef = referee && !DEF_RES[m.id];

  // Focus tracking for the x2 hint animation
  const [inputFocused, setInputFocused] = useState(false);

  // Exact score detection
  const parsedRes = parseResScore(res?.s);
  const exactScore =
    won &&
    !!parsedRes &&
    sA !== "" &&
    sB !== "" &&
    parseInt(sA) === parseInt(parsedRes[0]) &&
    parseInt(sB) === parseInt(parsedRes[1]);

  // Points
  const basePts = pick ? pickPts(m, pick) : 0;
  const earnedPts = exactScore ? basePts * 2 : won ? basePts : 0;

  // Win probabilities (p1/pX/p2 are already normalized to ~1)
  const pct1 = Math.round(m.p1 * 100);
  const pctX = Math.round(m.pX * 100);
  const pct2 = 100 - pct1 - pctX;

  const opts = [
    { key: "team1", pts: pickPts(m, "team1"), pct: pct1 },
    { key: "draw", pts: pickPts(m, "draw"), pct: pctX },
    { key: "team2", pts: pickPts(m, "team2"), pct: pct2 },
  ];

  // Implied pick from score inputs (pre-match)
  const impliedPick =
    sA !== "" && sB !== ""
      ? outFromScore(parseInt(sA, 10), parseInt(sB, 10))
      : null;

  const hasBetInProgress = !locked && impliedPick !== null;

  // Score values for post-match display
  const resA = parsedRes?.[0] ?? "?";
  const resB = parsedRes?.[1] ?? "?";

  // Which team side is dimmed (loser)
  const team1Dim = !!(res && res.o === "team2");
  const team2Dim = !!(res && res.o === "team1");

  // Auto-bet when score changes
  useEffect(() => {
    if (locked || sA === "" || sB === "") return;
    const a = parseInt(sA, 10);
    const b = parseInt(sB, 10);
    if (isNaN(a) || isNaN(b)) return;
    onBet(m.id, outFromScore(a, b), a, b);
  }, [sA, sB, locked]); // eslint-disable-line

  // Card border — only green (won) or gold (exact), default otherwise
  const cardBorder = exactScore
    ? C.gold
    : won
    ? C.turf
    : live
    ? `${C.live}66`
    : C.line;

  // Option badge styles
  const getBadgeStyle = (key: string): React.CSSProperties => {
    const isWinner = !!(res && res.o === key);
    const isUserPick = pick === key;
    const isImplied = !res && impliedPick === key;

    if (isImplied) {
      return {
        background: "transparent",
        border: "1.5px solid rgba(255,255,255,0.55)",
      };
    }

    if (res) {
      // After result: borders only, no color fill
      if (isUserPick && won) {
        // Our pick and we won → green border
        return { background: "transparent", border: `1.5px solid ${C.turf}` };
      }
      if (isUserPick) {
        // Our pick but we lost → white border + dark bg like default
        return { background: C.bg2, border: "1.5px solid rgba(255,255,255,0.85)" };
      }
      if (isWinner) {
        // Winner (not our pick) → green border
        return { background: "transparent", border: `1.5px solid ${C.turf}` };
      }
    }

    return { background: C.bg2, border: `1.5px solid ${C.line}` };
  };

  // Points text color inside badge
  const getPtsColor = (key: string): string => {
    const isImplied = !res && impliedPick === key;
    if (res) {
      return res.o === key ? "#fff" : "rgba(255,255,255,0.5)";
    }
    if (isImplied) return "#fff";
    return "rgba(255,255,255,0.5)";
  };

  // Percentage text color inside badge
  const getPctColor = (key: string): string => {
    const isImplied = !res && impliedPick === key;
    if (res) {
      return res.o === key ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.3)";
    }
    if (isImplied) return "rgba(255,255,255,0.7)";
    return "rgba(255,255,255,0.3)";
  };

  // Score input style
  const scoreInputStyle = (val: string): React.CSSProperties => ({
    width: 52,
    height: 52,
    textAlign: "center",
    outline: "none",
    fontFamily: "inherit",
    background: val !== "" ? C.bg2 : "#090E18",
    border: `2px solid ${val !== "" ? "rgba(255,255,255,0.2)" : C.line}`,
    borderRadius: 10,
    color: C.text,
    fontSize: 22,
    fontWeight: 800,
    padding: 0,
    transition: "border-color .15s",
    appearance: "textfield" as React.CSSProperties["appearance"],
  });

  // Prediction footer text
  const predText = noBet
    ? t("pronos.noBetPlaced")
    : sA !== "" && sB !== ""
    ? `${sA}-${sB}`
    : pick === "team1"
    ? m.t1
    : pick === "team2"
    ? m.t2
    : t("common.draw");

  return (
    <div
      style={{
        background: C.surface,
        border: `1.5px solid ${cardBorder}`,
        borderRadius: 16,
        marginBottom: 9,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header */}
      <div
        style={{
          textAlign: "center",
          padding: "10px 14px 0",
          fontSize: 11.5,
          fontWeight: 700,
          color: res ? C.muted : live ? C.live : C.muted,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {res ? t("pronos.matchFinished") : live ? <LiveBadge /> : `🇰🇷 ${kstTime(m)}`}
      </div>

      {/* Main content row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 8px 12px",
          gap: 4,
        }}
      >
        {/* Team 1 */}
        <div
          style={{
            flex: "0 0 76px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            opacity: team1Dim ? 0.4 : 1,
            transition: "opacity .2s",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: C.bg2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Fl team={m.t1} size={32} />
          </div>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: C.text,
              textAlign: "center",
              lineHeight: 1.2,
              maxWidth: 72,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {m.t1}
          </span>
        </div>

        {/* Center: score + option badges */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 9,
            position: "relative",
          }}
        >
          {/* Score area */}
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {!locked ? (
              /* Pre-match: two separate inputs */
              <>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="99"
                  value={sA}
                  onChange={(e) => {
                    setSA(e.target.value);
                    if (e.target.value !== "" && sB === "") setSB("0");
                  }}
                  style={scoreInputStyle(sA)}
                  onFocus={() => {
                    setInputFocused(true);
                    if (sA === "0") setSA("");
                  }}
                  onBlur={() => {
                    setInputFocused(false);
                    if (sA === "" && sB !== "") setSA("0");
                  }}
                />
                <span style={{ color: C.faint, fontWeight: 900, fontSize: 20 }}>
                  -
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="99"
                  value={sB}
                  onChange={(e) => {
                    setSB(e.target.value);
                    if (e.target.value !== "" && sA === "") setSA("0");
                  }}
                  style={scoreInputStyle(sB)}
                  onFocus={() => {
                    setInputFocused(true);
                    if (sB === "0") setSB("");
                  }}
                  onBlur={() => {
                    setInputFocused(false);
                    if (sB === "" && sA !== "") setSB("0");
                  }}
                />
              </>
            ) : res ? (
              /* Post-match: single box, gold border only for exact score */
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  border: `2px solid ${exactScore ? C.gold : C.line}`,
                  borderRadius: 10,
                  height: 52,
                  minWidth: 90,
                  padding: "0 14px",
                  fontSize: 26,
                  fontWeight: 800,
                  color: exactScore ? C.gold : C.text,
                  letterSpacing: 2,
                }}
              >
                {resA} - {resB}
              </div>
            ) : (
              /* Locked, no result yet: show entered score or dashes */
              <>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: C.bg2,
                    border: `2px solid ${C.line}`,
                    borderRadius: 10,
                    fontSize: 22,
                    fontWeight: 800,
                    color: sA ? C.text : C.faint,
                  }}
                >
                  {sA || "–"}
                </div>
                <span style={{ color: C.faint, fontWeight: 900, fontSize: 20 }}>
                  -
                </span>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: C.bg2,
                    border: `2px solid ${C.line}`,
                    borderRadius: 10,
                    fontSize: 22,
                    fontWeight: 800,
                    color: sB ? C.text : C.faint,
                  }}
                >
                  {sB || "–"}
                </div>
              </>
            )}
          </div>

          {/* Option badges */}
          <div style={{ display: "flex", gap: 6 }}>
            {opts.map((o) => (
              <div
                key={o.key}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 8,
                  padding: "5px 10px",
                  minWidth: 46,
                  gap: 1,
                  ...getBadgeStyle(o.key),
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 900,
                    color: getPtsColor(o.key),
                    lineHeight: 1.3,
                  }}
                >
                  {o.pts}
                </span>
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: getPctColor(o.key),
                    lineHeight: 1,
                  }}
                >
                  {o.pct}%
                </span>
              </div>
            ))}
          </div>

          {/* x2 hint — shows only when focused AND a score is entered */}
          {inputFocused && hasBetInProgress && (
            <div
              style={{
                position: "absolute",
                bottom: -24,
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: 10.5,
                fontWeight: 800,
                color: C.gold,
                whiteSpace: "nowrap",
                animation: "slideUp .2s ease",
              }}
            >
              🎯 Score exact = ×2
            </div>
          )}

          {/* Locked/live status */}
          {locked && !res && (
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: live ? C.live : C.faint,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {live ? (
                <>{t("pronos.liveBetsClosed")}</>
              ) : (
                <>
                  <Lock size={10} /> {t("pronos.betsClosed")}
                </>
              )}
            </div>
          )}
        </div>

        {/* Team 2 */}
        <div
          style={{
            flex: "0 0 76px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            opacity: team2Dim ? 0.4 : 1,
            transition: "opacity .2s",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: C.bg2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Fl team={m.t2} size={32} />
          </div>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: C.text,
              textAlign: "center",
              lineHeight: 1.2,
              maxWidth: 72,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {m.t2}
          </span>
        </div>
      </div>

      {/* Spacer when x2 hint is shown to avoid layout jump */}
      {!locked && <div style={{ height: inputFocused && hasBetInProgress ? 20 : 0, transition: "height .2s" }} />}

      {/* Footer — prediction result (only post-match) */}
      {res && (
        <div
          style={{
            borderTop: `1px solid ${C.lineSoft}`,
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.faint,
              flexShrink: 0,
            }}
          >
            {t("pronos.myPredictionLabel")}
          </span>
          <span
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 11.5,
              fontWeight: 700,
              color: noBet ? C.faint : C.text,
              whiteSpace: "nowrap",
            }}
          >
            {predText}
          </span>
          <span
            style={{
              marginLeft: "auto",
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 900,
              display: "flex",
              alignItems: "baseline",
              gap: 3,
            }}
          >
            {exactScore && (
              <span style={{ color: C.gold, fontWeight: 900, fontSize: 11 }}>
                x2
              </span>
            )}
            <span
              style={{
                color: earnedPts > 0 ? (exactScore ? C.gold : C.turf) : C.faint,
              }}
            >
              +{earnedPts}
            </span>
            <span style={{ fontSize: 9.5, color: C.faint }}>pts</span>
          </span>
        </div>
      )}

      {/* Referee panel */}
      {canRef && onSetResult && (
        <RefereeScore
          m={m}
          res={res}
          vals={refVals || { a: "", b: "" }}
          onUpdate={onRefScore || (() => {})}
          onSubmit={onSetResult}
        />
      )}
    </div>
  );
}

export function RefereeScore({
  m,
  res,
  vals,
  onUpdate,
  onSubmit,
}: {
  m: Match;
  res?: MatchResult;
  vals: { a: string; b: string };
  onUpdate: (field: string, value: string) => void;
  onSubmit: (id: string, outcome: string, score: string) => void;
}) {
  const a = vals.a || "";
  const b = vals.b || "";
  const na = a === "" ? null : parseInt(a, 10);
  const nb = b === "" ? null : parseInt(b, 10);
  const ready = na != null && nb != null && !isNaN(na) && !isNaN(nb);
  const winner = ready ? outFromScore(na!, nb!) : null;
  const wTxt =
    winner === "team1"
      ? m.t1
      : winner === "team2"
      ? m.t2
      : winner === "draw"
      ? "Draw"
      : "—";
  const inp: React.CSSProperties = {
    width: 52,
    textAlign: "center",
    background: C.bg,
    border: `1px solid ${C.line}`,
    borderRadius: 9,
    color: C.text,
    fontSize: 18,
    fontWeight: 800,
    padding: "9px 0",
    outline: "none",
    fontFamily: "inherit",
  };

  return (
    <div
      style={{
        margin: "0 12px 12px",
        borderTop: `1px dashed ${C.line}`,
        paddingTop: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: C.gold,
          marginBottom: 8,
          letterSpacing: 0.5,
          textTransform: "uppercase",
        }}
      >
        Real score (referee)
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11.5,
            fontWeight: 700,
          }}
        >
          <Fl team={m.t1} size={17} /> {m.t1}
        </span>
        <input
          type="number"
          inputMode="numeric"
          min="0"
          value={a}
          onChange={(e) => onUpdate("a", e.target.value)}
          style={inp}
        />
        <span style={{ color: C.faint, fontWeight: 800, fontSize: 16 }}>:</span>
        <input
          type="number"
          inputMode="numeric"
          min="0"
          value={b}
          onChange={(e) => onUpdate("b", e.target.value)}
          style={inp}
        />
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11.5,
            fontWeight: 700,
          }}
        >
          {m.t2} <Fl team={m.t2} size={17} />
        </span>
      </div>
      <div
        style={{
          textAlign: "center",
          fontSize: 11.5,
          marginTop: 7,
          color: ready ? C.turf : C.faint,
          fontWeight: 700,
        }}
      >
        {ready ? (
          <>
            Winner: <b>{wTxt}</b>
          </>
        ) : (
          "Enter both scores"
        )}
        {res && (
          <span style={{ color: C.muted, fontWeight: 600 }}>
            {" "}
            · current: {res.s}
          </span>
        )}
      </div>
      <button
        disabled={!ready}
        onClick={() => ready && onSubmit(m.id, winner!, `${na}–${nb}`)}
        style={{
          width: "100%",
          marginTop: 8,
          border: "none",
          borderRadius: 9,
          padding: "10px",
          fontWeight: 900,
          fontSize: 12.5,
          background: ready ? `linear-gradient(135deg,${C.gold},#c98f00)` : C.bg2,
          color: ready ? "#1a1200" : C.faint,
          fontFamily: "inherit",
          cursor: ready ? "pointer" : "default",
        }}
      >
        Confirm & distribute points
      </button>
    </div>
  );
}
